import React, { useState, useEffect, useMemo, useCallback, useContext, useRef, useDebugValue, useReducer } from "react";

import { helpers } from "@acadia-ui/theme";

import DataStore from "../../../Flux/Stores/PlacementDataStore";
import { fetchPlacements } from "../../../Flux/Actions/PlacementActions";
import LegacyPlacementView from "../features/LegacyViews/LegacyPlacementView";
import { TabContext } from "../AuditLevelTabs";
import useSyncStatuses from "../features/useSyncStatuses";
import useActions from "../features/useActions";
import useData from "../features/useData";
import useColumns from "../features/useColumns";
import useSearch from "../features/useSearch";

const classNameComponent = `gos-workbook__auditlevel-placement`;

function useFlip(initial = false) {
	const [flipped, setFlipped] = useState(initial);
	const flip = useCallback(() => {
		setFlipped((v) => !v);
		setTimeout(() => setFlipped(initial), 0);
	}, [initial]);
	return [flipped, flip];
}

export default function usePlacement({
	// this comment exists to prevent eslint from making this unreadable.
	auditLevel,
	parentCampaignUniqueID,
	metaData,
	auditLevels,
	fetchMetaData,
	placementAPI,
	JIRAStatus,
	campaignJiraTicketExists,
	selectedPackage,
	setSelectedPackage,
	selectedTab
}) {
	const [flipped, flip] = useFlip();
	const [rowHeight, setRowHeight] = useState("default");

	const {
		// this comment exists to prevent eslint from making this unreadable.
		data,
		config,
		refresh: refreshData,
		dataTableApi,
		setData,
		setConfig,
		renderedData,
		setRenderedData,
		fetch
	} = useData({
		parentCampaignUniqueID,
		auditLevel,
		fetchItems: fetchPlacements,
		DataStore,
		flip
	});

	// this was originally a useState, but it led to stale data being saved and referenced by various downstream components like actions and modals.
	// it's interface remains as a useState for backwards compatibility, but it is now a useReducer.
	const [selectedRows, setSelectedRows] = useReducer((prevState, newState) => {
		try {
			if (typeof newState === "function") {
				newState = newState(prevState) || prevState;
			}
			// grab the ids from the dataTableApi, use it to lookup the fresh data from the dataProvider
			const selectedNodes = dataTableApi.current.rowSelectionManager.tree.children.loadedNodes.filter(({ selectionState }) => selectionState === true);
			return selectedNodes.map(({ renderProps }) => {
				const rowData = dataTableApi.current.dataProvider.data.find(({ UniqueID }) => UniqueID === renderProps.value);
				return rowData || {};
			});
		} catch (error) {
			// just return the newState if there's an error
			return newState || [];
		}
	}, []);

	const refresh = useCallback(
		async (...args) => {
			const result = await refreshData(...args);
			if (!result?.aborted) {
				setSelectedRows([]);
			}
			return result;
		},
		[refreshData, setSelectedRows]
	);

	const { syncStatuses, displayPrismaStatusFilter, prismaAPIEnabled, platformSyncStatus } = useSyncStatuses({ campaignMetaData: metaData });
	const gcmStatus = useMemo(() => syncStatuses?.find(({ MasterPlatformID }) => MasterPlatformID == 1)?.SyncStatus, [syncStatuses]);
	const updateCounts = async (refreshMap) => {
		await refreshMap?.Package?.();
		await refreshMap?.Placement?.();
	};

	const updatePackages = async (refreshMap) => {
		await refreshMap?.Package?.();
	};

	const { modalComponents, actionBarProps, traffickingProps } = useActions({
		updateCounts,
		auditLevels,
		metaData,
		placementAPI,
		fetchMetaData,
		campaignJiraTicketExists,
		displayPrismaStatusFilter,
		prismaAPIEnabled,
		selectedRows,
		setSelectedRows,
		config,
		gcmStatus,
		renderedData,
		setRenderedData,
		dataTableApi,
		refresh,
		flip,
		fetch
	});

	const extraColumns = useColumns({ metaData, ...traffickingProps, gcmStatus, platformSyncStatus, auditLevels });

	const searchProps = useSearch({ setSelectedPackage });
	const [defaultFilterValues, setDefaultFilterValues] = useState([]);

	useEffect(() => {
		dataTableApi?.current?.toolbarApi?.current?.setSearchValue(searchProps.searchValue);
		searchProps.onSearch(searchProps.searchValue);
	}, [selectedTab, dataTableApi, searchProps]);

	useEffect(() => {
		if (selectedPackage) {
			const parentKey = config?.ParentData?.find((key) => {
				return key.Name === "ParentDescription";
			});
			const parentOption = parentKey?.Options?.find((option) => {
				return option.OptionID === selectedPackage.PackageID;
			});

			if (parentOption) {
				setDefaultFilterValues([
					{
						dataKey: parentKey,
						dataValue: [{ ...parentOption, value: parentOption.OptionID, label: parentOption.OptionLabel }]
					}
				]);
			}
		} else {
			setDefaultFilterValues([]);
		}
	}, [config, selectedPackage, dataTableApi]);

	helpers.useAsyncEffect(() => {
		if (parentCampaignUniqueID && auditLevel && metaData.CampaignUniqueIDCode == parentCampaignUniqueID && auditLevel.PlatformID == metaData.PlatformID) {
			refresh(parentCampaignUniqueID, auditLevel);
		}
	}, [parentCampaignUniqueID, auditLevel]);

	const Component = useCallback(
		({ refreshMap }) => {
			if (data && renderedData && config && metaData && fetchMetaData) {
				return (
					<div className={`gos-workbook__auditlevel ${classNameComponent}`}>
						<TabContext.Provider value={{}}>
							<LegacyPlacementView
								dataTableApi={dataTableApi}
								campaignMetaData={metaData}
								placementAPI={placementAPI}
								JIRAStatus={JIRAStatus}
								auditLevelData={auditLevels}
								updateCounts={async () => await updateCounts(refreshMap)}
								updatePackages={async () => await updatePackages(refreshMap)}
								updateMetaData={fetchMetaData}
								campaignJiraTicketExists={campaignJiraTicketExists}
								selectedRows={selectedRows}
								setSelectedRows={setSelectedRows}
								actionBarProps={actionBarProps}
								clientTMEnabled={traffickingProps.clientTMEnabled}
								campaignTMEnabled={traffickingProps.campaignTMEnabled}
								displayPrismaStatusFilter={displayPrismaStatusFilter}
								TMEnabled={traffickingProps.TMEnabled}
								gcmStatus={gcmStatus}
								originalData={data}
								renderedData={renderedData}
								setRenderedData={setRenderedData}
								config={config}
								refresh={refresh}
								platformSyncStatus={platformSyncStatus}
								extraColumns={extraColumns}
								searchProps={searchProps}
								selectedPackage={selectedPackage}
								setSelectedPackage={setSelectedPackage}
								flip={flip}
								defaultFilterValues={defaultFilterValues}
								rowHeight={rowHeight}
								setRowHeight={setRowHeight}
								fetch={fetch}
							/>
						</TabContext.Provider>
					</div>
				);
			}
			return <></>;
		},
		[
			// this comment exists to prevent eslint from making this unreadable.
			config,
			renderedData,
			setRenderedData,
			data,
			setData,
			metaData,
			fetchMetaData,
			campaignJiraTicketExists,
			selectedRows,
			setSelectedRows,
			traffickingProps,
			gcmStatus,
			actionBarProps,
			updateCounts,
			refresh,
			platformSyncStatus,
			extraColumns,
			extraColumns.length,
			JSON.stringify(extraColumns),
			searchProps,
			searchProps.searchValue,
			searchProps.searchElement,
			searchProps.onSearch,
			dataTableApi,
			selectedPackage,
			setSelectedPackage,
			JIRAStatus,
			placementAPI,
			displayPrismaStatusFilter,
			flip,
			defaultFilterValues,
			rowHeight,
			setRowHeight,
			fetch
		]
	);

	const modals = useMemo(() => {
		if (data && config && metaData) {
			return modalComponents?.map((Modal, i) => <Modal key={i} />);
		}
		return <></>;
	}, [modalComponents, config, metaData]);

	// AuditLevelHooks watches this deps array and rerenders when they change
	const deps = useMemo(
		() => ({
			extraColumns,
			flipped
		}),
		[extraColumns, flipped]
	);

	return useMemo(
		() => ({
			Component,
			data,
			refresh,
			renderedData,
			modals,
			deps
		}),
		[Component, data, refresh, selectedRows, renderedData, modals, deps]
	);
}
